import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { X, ChevronLeft, ChevronRight, Lock } from "lucide-react";
import { clsx } from "clsx";
import { Tooltip } from "@maeiz/ui";
import { useAuth } from "@stores/auth";
import { useLanguageStore } from "@stores/language";
import { isRtl } from "@lib/i18n/languages";
import { planAllows } from "@lib/entitlements";
import { Avatar } from "@components/Avatar";
import {
    ACCOUNT_NAV,
    BILLING_ONLY_NAV_PATHS,
    MAIN_NAV,
    type NavItem,
} from "./navConfig";

interface TenantSidebarProps {
    isMobileOpen?: boolean;
    onMobileClose?: () => void;
    isCollapsed?: boolean;
    onToggleCollapse?: () => void;
}

const isActivePath = (pathname: string, path: string) =>
    path === "/" ? pathname === "/" : pathname === path || pathname.startsWith(`${path}/`);

export const TenantSidebar: React.FC<TenantSidebarProps> = ({
    isMobileOpen = false,
    onMobileClose,
    isCollapsed = false,
    onToggleCollapse,
}) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { user, billingOnly } = useAuth();
    const rtl = isRtl(useLanguageStore((s) => s.language));
    const plan = user?.plan ?? null;

    const renderItem = (item: NavItem, collapsed: boolean) => {
        const label = t(`nav.${item.navKey}`);
        const Icon = item.icon;
        const locked = !planAllows(plan, item.feature);
        const disabled = billingOnly && !BILLING_ONLY_NAV_PATHS.includes(item.path);
        const active = isActivePath(pathname, item.path);
        const lockText = t("nav.lockedFeature");

        const content = (
            <>
                <div className="relative flex items-center justify-center">
                    <div
                        className={clsx(
                            "flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-200",
                            active
                                ? "bg-gradient-to-br from-primary to-secondary text-white shadow-md shadow-primary/20"
                                : "bg-neutral-light/60 text-neutral-dark/80 group-hover:bg-primary/10 group-hover:text-primary dark:bg-white/5 dark:text-neutral-light/80 dark:group-hover:bg-primary/20 dark:group-hover:text-primary-light"
                        )}
                    >
                        <Icon size={18} className="transition-all duration-200" />
                    </div>
                    {collapsed && locked && (
                        <span
                            className="absolute -top-1 -end-1 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-white shadow-sm ring-2 ring-white dark:ring-neutral-dark scale-90"
                            title={lockText}
                            aria-label={lockText}
                        >
                            <Lock size={9} strokeWidth={2.5} aria-hidden />
                        </span>
                    )}
                </div>
                {!collapsed && (
                    <>
                        <span className="truncate text-xs font-semibold leading-tight">{label}</span>
                        {locked && (
                            <Tooltip content={lockText} placement="top">
                                <Lock size={13} className="ms-auto flex-shrink-0 text-amber-500 dark:text-amber-400" aria-label={lockText} />
                            </Tooltip>
                        )}
                        {active && !locked && (
                            <div className="absolute end-2 h-5 w-1 rounded-full bg-gradient-to-b from-primary to-secondary" />
                        )}
                    </>
                )}
            </>
        );

        const cls = clsx(
            "group relative flex items-center transition-all duration-200",
            collapsed
                ? "mx-auto h-10 w-10 justify-center rounded-xl p-0"
                : "gap-2.5 rounded-xl px-2.5 py-2",
            active
                ? "bg-gradient-to-r from-primary/15 via-primary/5 to-transparent text-neutral-dark shadow-sm shadow-primary/5 dark:from-primary/25 dark:via-primary/10 dark:text-white rtl:bg-gradient-to-l font-semibold"
                : "text-neutral-dark/70 hover:bg-neutral-light/60 hover:text-neutral-dark dark:text-neutral-light/70 dark:hover:bg-white/5 dark:hover:text-white",
            disabled && "pointer-events-none opacity-40"
        );

        const linkEl = (
            <Link
                key={item.path}
                to={item.path}
                onClick={onMobileClose}
                className={cls}
                aria-disabled={disabled || undefined}
                tabIndex={disabled ? -1 : undefined}
                aria-current={active ? "page" : undefined}
            >
                {content}
            </Link>
        );

        if (collapsed) {
            return (
                <Tooltip key={item.path} content={label} placement={rtl ? "left" : "right"}>
                    <div className="flex justify-center">{linkEl}</div>
                </Tooltip>
            );
        }

        return linkEl;
    };

    const NavContent = ({ collapsed }: { collapsed: boolean }) => (
        <nav
            className={clsx(
                "sidebar-scroll flex-1 overflow-y-auto",
                collapsed ? "space-y-2 px-1.5 py-3" : "space-y-2.5 px-2.5 py-3"
            )}
            aria-label={t("nav.navigation")}
        >
            {MAIN_NAV.map((group, i) => (
                <div key={group.groupKey ?? `g${i}`} className="space-y-1">
                    {group.groupKey && !collapsed && (
                        <p className="px-2.5 pt-1.5 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-neutral-dark/45 dark:text-neutral-light/45">
                            {t(`nav.groups.${group.groupKey}`)}
                        </p>
                    )}
                    {group.items.map((item) => renderItem(item, collapsed))}
                </div>
            ))}
            {!collapsed && <div className="mx-2 my-2 border-t border-neutral-border/30 dark:border-white/10" />}
            {collapsed && <div className="mx-auto my-2 w-6 border-t border-neutral-border/30 dark:border-white/10" />}
            <div className="space-y-1">{ACCOUNT_NAV.map((item) => renderItem(item, collapsed))}</div>
        </nav>
    );

    const Chevron = isCollapsed ? (rtl ? ChevronLeft : ChevronRight) : rtl ? ChevronRight : ChevronLeft;

    return (
        <>
            <aside
                className={clsx(
                    "fixed bottom-0 start-0 top-16 z-40 hidden border-e border-neutral-border/30 bg-white/70 backdrop-blur-xl transition-all duration-300 dark:border-white/10 dark:bg-neutral-dark/70 lg:block",
                    isCollapsed ? "w-16" : "w-60"
                )}
            >
                <div className="flex h-full flex-col">
                    <NavContent collapsed={isCollapsed} />
                    {onToggleCollapse && (
                        <div className={clsx("flex-shrink-0 border-t border-neutral-border/30 p-2 dark:border-white/10")}>
                            <button
                                type="button"
                                onClick={onToggleCollapse}
                                className={clsx(
                                    "flex items-center rounded-xl text-neutral-dark/70 transition-all duration-200 hover:bg-neutral-light/60 hover:text-neutral-dark dark:text-neutral-light/70 dark:hover:bg-white/5 dark:hover:text-white",
                                    isCollapsed ? "mx-auto h-9 w-9 justify-center p-0" : "w-full justify-end px-2.5 py-1.5"
                                )}
                                title={isCollapsed ? t("nav.sidebar.expand") : t("nav.sidebar.collapse")}
                                aria-label={isCollapsed ? t("nav.sidebar.expand") : t("nav.sidebar.collapse")}
                            >
                                <Chevron size={18} />
                            </button>
                        </div>
                    )}
                </div>
            </aside>
            {isMobileOpen && (
                <>
                    <div
                        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
                        onClick={onMobileClose}
                    />
                    <aside className="fixed bottom-0 start-0 top-0 z-50 w-72 max-w-[85vw] border-e border-neutral-border/50 bg-white shadow-2xl dark:border-neutral-border/20 dark:bg-neutral-dark lg:hidden">
                        <div className="flex h-full flex-col">
                            <div className="flex items-center justify-between border-b border-neutral-border/50 p-4 dark:border-neutral-border/20">
                                <div className="flex items-center gap-3">
                                    <Avatar name={user?.name ?? t("common.brand")} src={user?.profilePicture} size="md" />
                                    <span className="block text-base font-bold text-neutral-dark dark:text-white">
                                        {t("common.brand")}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={onMobileClose}
                                    aria-label={t("nav.sidebar.closeMenu")}
                                    className="rounded-xl p-2 transition-colors duration-150 hover:bg-neutral-light/50 dark:hover:bg-white/5"
                                >
                                    <X size={20} className="text-neutral-dark dark:text-neutral-light" />
                                </button>
                            </div>
                            <div className="border-b border-neutral-border/30 px-4 py-2.5 dark:border-neutral-border/10">
                                <h2 className="text-[10px] font-bold uppercase tracking-wider text-neutral-dark/40 dark:text-neutral-light/40">
                                    {t("nav.portal")}
                                </h2>
                            </div>
                            <NavContent collapsed={false} />
                        </div>
                    </aside>
                </>
            )}
        </>
    );
};
